import Phaser from 'phaser';
import { t } from '@i18n/index';
import { toWorld, distanceNm, initialBearingDeg, TRIANGLE_VERTICES, type LatLon } from '@core/geo';
import { ZoomDirector, zoomFactorFor, VIEWPORT_WIDTH_NM, type ZoomLevel } from '@core/map/zoom';
import { ALL_PLACES, HOME_BASE, type Place } from '@core/world/places';
import type { Route } from '@core/map/navigation';
import { incidentType } from '@core/mission/incident';
import type { OutcomeTier, TierForecast } from '@core/mission/outcome';
import { travelHours } from '@core/geo';
import { RESEARCH_SHIP } from '@core/voyage/profiles';
import { showBriefing } from '../ui/briefingPanel';
import type { HudScene } from './HudScene';
import type { MapHudScene } from './MapHudScene';
import { buildMapTexture } from '../render/mapTexture';
import type { MapData } from '../mapData';
import { PALETTE, FONT, px } from '../theme';
import type { ActiveMission, Campaign } from '@core/campaign/campaign';
import type { Incident } from '@core/mission/incident';
import { vesselProfile, type VesselId } from '@core/voyage/profiles';
import { seaStateKey } from '@core/voyage/weather';
import type { VoyageChoice, VoyageEvent } from '@core/voyage/events';
import { audio } from '../audio/engine';
import { DRONE_ROOT, VOYAGE_MOTIF } from '../audio/motifs';

/**
 * Wie viele Spielstunden auf eine reale Sekunde entfallen.
 *
 * In M1 grosszuegig gewaehlt, damit sich die Zoomstufen in Sekunden statt in
 * Minuten vorfuehren lassen. Die Reise-Simulation aus M2 ersetzt das durch ein
 * Zeitmodell mit Wetter, Treibstoff und echtem Zeitdruck.
 */
const GAME_HOURS_PER_SECOND = 2;

/**
 * Die Farbe eines Notruf-Markers.
 *
 * Sie zeigt, was bei sofortigem Auslaufen und voller Fahrt noch zu erreichen
 * waere — dieselbe Staffelung wie in der frueheren Notrufliste, nur jetzt am
 * Ort des Geschehens statt in einer Tabelle.
 */
const TIER_MARKER_COLOUR: Readonly<Record<OutcomeTier, number>> = {
  rescue: 0x8fd9c4,
  salvage: 0xe0b070,
  cold: 0x7f95a8,
  nothing: 0xc9736f,
};

export interface HudState {
  readonly position: LatLon;
  readonly depthMetres: number;
  readonly aground: boolean;
  readonly underway: boolean;
  readonly speedKnots: number;
  readonly nearestNameKey: string;
  readonly nearestDistanceNm: number;
  readonly zoomLevel: ZoomLevel;
  /** Verbleibende Strecke zum Einsatzort. */
  readonly remainingNm: number;
  readonly fuel: number;
  readonly hull: number;
  readonly seaStateKey: string;
  /** Stufe, die bei Ankunft zum jetzigen Zeitpunkt herauskaeme. */
  readonly projectedTier: string;
}

const ZOOM_ORDER: readonly ZoomLevel[] = ['strategic', 'transit', 'tactical'];

export class WorldMapScene extends Phaser.Scene {
  private mapData!: MapData;
  private campaign!: Campaign;
  /**
   * Welchem Fahrzeug Kamera und Anzeigen folgen.
   *
   * Es koennen zwei gleichzeitig unterwegs sein; sehen kann man aber immer nur
   * eines aus der Naehe. Die Wahl trifft der Spieler, und sie aendert nichts
   * am Spielstand — beide fahren weiter.
   */
  private focus: VesselId = 'ship';
  /** Marker der offenen Notrufe im Lagebild. */
  private incidentMarkers: Phaser.GameObjects.Container[] = [];
  /** Wie viele Notrufe zuletzt offen standen — dient dem Erkennen neuer. */
  private knownIncidentIds = new Set<string>();
  private director!: ZoomDirector;

  /** Je Fahrzeug ein Marker — beide sind immer zu sehen. */
  private vesselMarkers = new Map<VesselId, Phaser.GameObjects.Container>();
  private courseLine!: Phaser.GameObjects.Graphics;
  private wakeGraphics!: Phaser.GameObjects.Graphics;
  /** Zurueckgelegte Positionen je Fahrzeug — das Kielwasser dahinter. */
  private wake = new Map<VesselId, LatLon[]>();
  private coastGraphics!: Phaser.GameObjects.Graphics;
  private overlayGraphics!: Phaser.GameObjects.Graphics;
  private contourLayer!: Phaser.GameObjects.Image;
  private placeMarkers: Phaser.GameObjects.Container[] = [];

  /** Vom Spieler erzwungene Stufe. */
  private manualLevel: ZoomLevel | null = null;

  /** Sobald das Ziel erreicht ist, uebernimmt die Einsatzort-Szene. */
  private handedOver = false;

  private appliedZoom = -1;
  private lastDrawnLevel: ZoomLevel | null = null;
  private cameraMode: ZoomLevel | null = null;
  private mapRect!: { x: number; y: number; width: number; height: number };

  /**
   * Das Ereignis, das gerade eine Entscheidung verlangt — samt der Fahrt, zu
   * der es gehoert.
   *
   * Solange eines ansteht, stehen **alle** Fahrten still. Das ist Absicht: Die
   * Entscheidung soll ueberlegt getroffen werden, und es waere unfair, wenn
   * dem anderen Fahrzeug waehrenddessen die Zeit davonliefe.
   */
  private pendingEvent: { mission: ActiveMission; event: VoyageEvent } | null = null;

  constructor() {
    super('WorldMap');
  }

  /**
   * Phaser verwendet bei `scene.start()` dieselbe Szenen-Instanz wieder —
   * Feldinitialisierungen laufen also nur ein einziges Mal, beim Konstruieren.
   * Ohne dieses ausdrueckliche Zuruecksetzen behielte die Szene beim zweiten
   * Auslaufen ihren alten Zustand, und die Vergleiche in `applyZoom` und
   * `updateCameraMode` wuerden sofort abbrechen: falscher Zoom, keine Kamera,
   * keine Küstenlinien.
   */
  init(): void {
    this.manualLevel = null;
    this.handedOver = false;
    this.wake = new Map();
    this.vesselMarkers = new Map();
    this.pendingEvent = null;
    this.appliedZoom = -1;
    this.lastDrawnLevel = null;
    this.cameraMode = null;
    this.placeMarkers = [];
    this.incidentMarkers = [];
    this.focus = 'ship';
  }

  create(): void {
    this.mapData = this.registry.get('mapData') as MapData;
    this.campaign = this.registry.get('campaign') as Campaign;

    this.cameras.main.setBackgroundColor(PALETTE.abyss);
    this.mapRect = this.drawMap();
    this.coastGraphics = this.add.graphics();
    this.overlayGraphics = this.add.graphics();
    this.courseLine = this.add.graphics();
    this.wakeGraphics = this.add.graphics();
    this.createPlaceMarkers();
    this.setupInput();

    this.director = new ZoomDirector();
    this.createVesselMarkers();
    this.refreshIncidentMarkers();
    this.knownIncidentIds = new Set(this.campaign.activeIncidents.map((i) => i.id));

    // Die Kamera folgt einem Fahrzeug, das faehrt — sonst zeigt sie das ganze
    // Dreieck.
    this.focus = this.campaign.activeMissions[0]?.vesselId ?? 'ship';
    this.manualLevel = this.campaign.activeMissions.length === 0 ? 'strategic' : null;
    this.applyZoom(false);
    this.updateCameraMode(this.activeLevel(), false);
    this.redrawStatic();

    this.scene.launch('MapHud');
    // Die Fahrtanzeige gehoert zur Fahrt. Im Lagebild stuenden dort sieben
    // Zeilen mit Gedankenstrichen — Platzverbrauch ohne Aussage.
    this.scene.launch('Hud');
    this.syncHudVisibility();
    this.setAudio();

    this.cameras.main.fadeIn(400, 5, 8, 13);
  }

  /**
   * Der Klang richtet sich danach, ob ueberhaupt jemand unterwegs ist.
   *
   * An der Station ist es ruhig; sobald ein Fahrzeug faehrt, laeuft das Sonar
   * und das Echo kommt vom Ziel der Fahrt, der die Kamera folgt.
   */
  private setAudio(): void {
    const focused = this.campaign.missionOf(this.focus);
    const underway = this.campaign.activeMissions.length > 0;

    audio.startSea();
    audio.onPing = null;

    if (!underway) {
      const atBase = this.campaign.isAtBase('ship') || this.campaign.isAtBase('submarine');
      audio.startDrone(atBase ? DRONE_ROOT.base : DRONE_ROOT.site);
      audio.setSubmerged(false);
      audio.stopSonarSweep();
      audio.setShallowWater(atBase);
      audio.setTension(atBase ? 0.15 : 0.4);
      if (this.campaign.activeIncidents.length > 0) audio.cue('alert');
      return;
    }

    const submerged = this.focus === 'submarine';
    audio.startDrone(DRONE_ROOT.voyage);
    audio.setSubmerged(submerged);
    // Auf See laeuft das Sonar durchgehend — das ist das Geraeusch einer Suche.
    audio.startSonarSweep(submerged ? 7 : 11);
    // Jeder Ping wirft ein Echo zurueck. Die Laufzeit ist echt gerechnet: je
    // naeher das Ziel, desto frueher und lauter die Antwort. Damit wird aus
    // Klangkulisse eine Entfernungsangabe, die man hoert statt abzulesen.
    if (focused !== null) {
      audio.onPing = () => audio.sonarEcho(focused.voyage.state.remainingNm, true);
    }
    audio.playMotif(VOYAGE_MOTIF, 0.06);
  }

  /** Blendet die Fahrtanzeige ein oder aus, je nachdem ob jemand faehrt. */
  private syncHudVisibility(): void {
    const hud = this.scene.get('Hud') as HudScene | undefined;
    hud?.setPanelVisible(this.campaign.missionOf(this.focus) !== null);
  }

  /** Wechselt, welchem Fahrzeug Kamera und Anzeigen folgen. */
  setFocus(vesselId: VesselId): void {
    if (this.focus === vesselId) return;
    this.focus = vesselId;
    this.cameraMode = null;
    this.updateCameraMode(this.activeLevel(), true);
    this.syncHudVisibility();
    this.setAudio();
  }

  get focused(): VesselId {
    return this.focus;
  }

  // ------------------------------------------------------------------ Aufbau

  private drawMap(): { x: number; y: number; width: number; height: number } {
    const texture = buildMapTexture(this.mapData.bathymetry);
    const { x, y, width, height } = texture.world;

    const place = (key: string, canvas: HTMLCanvasElement): Phaser.GameObjects.Image => {
      // Ein bereits vorhandener Schluessel wuerde beim Szenenwechsel kollidieren.
      if (this.textures.exists(key)) {
        this.textures.remove(key);
      }
      this.textures.addCanvas(key, canvas);
      return this.add.image(x, y, key).setOrigin(0, 0).setDisplaySize(width, height);
    };

    place('chart', texture.base);
    this.contourLayer = place('chart-contours', texture.contours);

    return texture.world;
  }

  /** Wohin eine Fahrt unterwegs ist. */
  private targetOf(mission: ActiveMission): LatLon {
    return mission.homebound ? HOME_BASE.position : (mission.incident as Incident).position;
  }

  private labelOf(mission: ActiveMission): string {
    return mission.homebound
      ? t(HOME_BASE.nameKey)
      : (mission.incident as Incident).vesselName;
  }

  /**
   * Ein Marker, der sendet: ein fester Ring, ein Punkt und ein zweiter Ring,
   * der nach aussen laeuft und verblasst. Das zieht den Blick genau dorthin,
   * wo etwas passiert.
   *
   * `pulseMs` traegt dabei die Dringlichkeit: Je knapper die Frist, desto
   * schneller der Takt. Man liest die Lage damit, ohne eine Zahl zu lesen.
   */
  private pulsingMarker(
    position: LatLon,
    label: string,
    colour: number,
    pulseMs: number,
  ): Phaser.GameObjects.Container {
    const world = toWorld(position);

    const ring = this.add.circle(0, 0, 9);
    ring.setStrokeStyle(1.5, colour);

    const pulse = this.add.circle(0, 0, 9);
    pulse.setStrokeStyle(1.5, colour);
    this.tweens.add({
      targets: pulse,
      scale: 3.2,
      alpha: { from: 0.7, to: 0 },
      duration: pulseMs,
      repeat: -1,
      ease: 'Sine.easeOut',
    });

    const dot = this.add.circle(0, 0, 2, colour);
    const text = this.add.text(13, -6, label, {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: `#${colour.toString(16).padStart(6, '0')}`,
    });

    return this.add.container(world.x, world.y, [pulse, ring, dot, text]);
  }

  /**
   * Beide eigenen Fahrzeuge, immer sichtbar.
   *
   * Sie stehen an verschiedenen Orten und koennen seit M9 auch gleichzeitig
   * unterwegs sein. Ein Klick holt die Kamera zu einem von beiden — man kann
   * nicht ueberall zugleich hinsehen, aber man kann waehlen.
   */
  private createVesselMarkers(): void {
    for (const id of ['ship', 'submarine'] as const) {
      const profile = vesselProfile(id);
      const world = toWorld(this.campaign.positionOf(id));

      // Eine schlichte Pfeilspitze; die Drehung zeigt den anliegenden Kurs.
      const hull = this.add.triangle(0, 0, 0, -7, 4.5, 6, -4.5, 6, PALETTE.vessel);
      const halo = this.add.circle(0, 0, 10);
      halo.setStrokeStyle(1, PALETTE.vessel, 0.3);
      /*
       * Die Beschriftungen auseinanderziehen.
       *
       * Solange beide Fahrzeuge an der Station liegen, stehen sie auf
       * demselben Punkt — und mit dem Namen des Ortes daneben wurde daraus ein
       * unlesbarer Klumpen aus drei uebereinanderliegenden Zeilen. Das Schiff
       * schreibt jetzt nach rechts, das Boot nach links und tiefer.
       */
      const toLeft = id === 'submarine';
      const label = this.add.text(toLeft ? -12 : 12, toLeft ? 6 : -16, t(profile.nameKey), {
        fontFamily: FONT.mono,
        fontSize: px(10),
        color: PALETTE.inkDim,
      });
      if (toLeft) label.setOrigin(1, 0);

      const marker = this.add.container(world.x, world.y, [halo, hull, label]);
      marker.setSize(40, 40);
      // Phaser wertet die Trefferflaeche eines Containers **um ihren
      // Mittelpunkt** aus, nicht ab der linken oberen Ecke — x/y sind also ein
      // Versatz, keine Ecke. Mit (-20, -20) lag die Flaeche daneben und der
      // Marker war nicht anklickbar. Derselbe Fallstrick steht in `panel.ts`
      // schon beschrieben.
      marker.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, 40, 40),
        Phaser.Geom.Rectangle.Contains,
      );
      marker.input!.cursor = 'pointer';
      marker.on('pointerdown', () => {
        audio.resume();
        audio.cue('select');
        this.setFocus(id);
      });

      this.vesselMarkers.set(id, marker);
      this.wake.set(id, []);
    }
  }

  /**
   * Zeichnet die Notruf-Marker neu.
   *
   * Farbe und Takt tragen die Lage: Die Farbe zeigt, was bei sofortigem
   * Auslaufen und voller Fahrt noch zu erreichen waere, der Pulsschlag, wie
   * knapp es wird. Ein Klick oeffnet die Einsatzplanung ueber der Karte —
   * seit M9 auch dann, wenn schon ein Fahrzeug unterwegs ist. Genau darum
   * geht es: Man kann umdisponieren.
   */
  private refreshIncidentMarkers(): void {
    for (const marker of this.incidentMarkers) marker.destroy();
    this.incidentMarkers = [];

    // Ziele laufender Fahrten bekommen eine eigene, auffaelligere Farbe.
    const targeted = new Set(
      this.campaign.activeMissions
        .map((m) => m.incident?.id)
        .filter((id): id is string => id !== undefined),
    );

    for (const incident of this.campaign.activeIncidents) {
      const aimedAt = targeted.has(incident.id);
      const forecast = this.reachableForecast(incident);
      const colour = aimedAt ? PALETTE.triangle : TIER_MARKER_COLOUR[forecast.tier];

      // Vom Alter zur Dringlichkeit: frisch schlaegt ruhig, knapp schlaegt schnell.
      const age = this.campaign.ageOf(incident);
      const urgency = Math.min(1, age / Math.max(1, incident.estimatedWindows.coldHours));
      const pulseMs = 2800 - urgency * 1800;

      const marker = this.pulsingMarker(
        incident.position,
        `${t(incidentType(incident.kind).nameKey)} · ${incident.vesselName}`,
        colour,
        pulseMs,
      );

      // Die Trefferflaeche muss grosszuegig sein: In der Uebersicht ist der
      // Marker wenige Bildpunkte gross.
      marker.setSize(48, 48);
      marker.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, 48, 48),
        Phaser.Geom.Rectangle.Contains,
      );
      marker.input!.cursor = 'pointer';
      marker.on('pointerdown', () => {
        audio.resume();
        audio.cue('select');
        this.openBriefing(incident);
      });

      this.incidentMarkers.push(marker);
    }

    this.counterScaleMarkers();
  }

  /** Was bei sofortigem Auslaufen und voller Fahrt noch zu erreichen waere. */
  private reachableForecast(incident: Incident): TierForecast {
    const hours = travelHours(
      this.campaign.distanceTo(incident, this.focus),
      RESEARCH_SHIP.knots.full,
    );
    return this.campaign.forecastOnArrival(incident, this.campaign.clockHours + hours);
  }

  /**
   * Zeichnet das Lagebild neu, nachdem Zeit vergangen ist oder sich ein
   * Auftrag geaendert hat.
   */
  refreshIdle(): void {
    const current = this.campaign.activeIncidents;
    const fresh = current.filter((incident) => !this.knownIncidentIds.has(incident.id));
    this.knownIncidentIds = new Set(current.map((incident) => incident.id));

    this.refreshIncidentMarkers();
    this.updateVesselMarkers();
    this.redrawOverlays();
    this.syncHudVisibility();
    this.setAudio();
    if (fresh.length > 0) audio.cue('alert');
    this.announceLapsed();
  }

  /**
   * Oeffnet die Einsatzplanung als Tafel ueber der Karte.
   *
   * Gezeichnet wird sie in die HUD-Szene, nicht in diese hier: Die Kamera
   * dieser Szene ist gezoomt, und `setScrollFactor(0)` gleicht Verschiebung
   * aus, nicht Vergroesserung. Die Tafel laege sonst verkleinert da, und die
   * Trefferflaechen der Schaltflaechen neben dem, was zu sehen ist.
   *
   * Der Kurs dagegen gehoert in *diese* Szene: Er liegt in Kartenkoordinaten
   * und soll mitskalieren.
   */
  private openBriefing(incident: Incident): void {
    const hud = this.scene.get('MapHud') as Phaser.Scene | undefined;
    if (hud === undefined) return;

    showBriefing(hud, {
      campaign: this.campaign,
      incident,
      onCourse: (route) => this.drawPlannedCourse(route),
      onDepart: (order) => {
        this.campaign.dispatch({
          vesselId: order.vesselId,
          incident: order.incident,
          speed: order.speed,
        });
        this.setFocus(order.vesselId);
        this.manualLevel = null;
        this.wake.set(order.vesselId, []);
        this.refreshIdle();
        (this.scene.get('MapHud') as MapHudScene | undefined)?.refresh();
      },
      onClose: () => this.redrawOverlays(),
    });
  }

  /**
   * Zeichnet den geplanten Kurs, waehrend die Tafel offen ist.
   *
   * Der eigentliche Gewinn des Kartenhubs: Man sieht den Umweg um die
   * Baenke, sobald man Fahrzeug oder Fahrtstufe wechselt, statt ihn als
   * Prozentzahl zu lesen.
   */
  private drawPlannedCourse(route: Route): void {
    this.courseLine.clear();
    if (route.waypoints.length < 2) return;

    const zoom = this.cameras.main.zoom || 1;
    this.courseLine.lineStyle(1.5 / zoom, PALETTE.course, 0.8);
    this.courseLine.beginPath();

    const first = toWorld(route.waypoints[0] as LatLon);
    this.courseLine.moveTo(first.x, first.y);
    for (let i = 1; i < route.waypoints.length; i++) {
      const point = toWorld(route.waypoints[i] as LatLon);
      this.courseLine.lineTo(point.x, point.y);
    }
    this.courseLine.strokePath();

    this.courseLine.fillStyle(PALETTE.course, 0.9);
    for (let i = 1; i < route.waypoints.length - 1; i++) {
      const point = toWorld(route.waypoints[i] as LatLon);
      this.courseLine.fillCircle(point.x, point.y, 2.5 / zoom);
    }
  }

  private createPlaceMarkers(): void {
    for (const place of ALL_PLACES) {
      const world = toWorld(place.position);
      const colour = place.kind === 'base' ? PALETTE.base : PALETTE.landmark;

      const ring = this.add.circle(0, 0, 6);
      ring.setStrokeStyle(1.5, colour);
      const dot = this.add.circle(0, 0, 1.5, colour);

      const label = this.add
        .text(11, -6, t(place.nameKey), {
          fontFamily: FONT.mono,
          fontSize: px(11),
          color: place.kind === 'base' ? '#8fd9c4' : '#e0b070',
        })
        .setOrigin(0, 0);

      const container = this.add.container(world.x, world.y, [ring, dot, label]);
      this.placeMarkers.push(container);
    }
  }

  private setupInput(): void {
    // Der Kurs liegt fest — das Mausrad dient nur dem Betrachten der Karte.
    this.input.on('wheel', (_p: unknown, _o: unknown, _dx: number, dy: number) => {
      const base = this.manualLevel ?? this.director.current;
      const index = ZOOM_ORDER.indexOf(base);
      const next = Math.max(0, Math.min(ZOOM_ORDER.length - 1, index + (dy > 0 ? -1 : 1)));
      this.manualLevel = ZOOM_ORDER[next] as ZoomLevel;
    });
  }

  // ------------------------------------------------------------------ Ablauf

  override update(_time: number, deltaMs: number): void {
    // Solange ein Ereignis auf eine Entscheidung wartet, stehen **alle**
    // Fahrten still. Sonst waere der Zeitdruck des Ereignisses eine
    // Reaktionsuebung, und dem anderen Fahrzeug liefe die Zeit davon,
    // waehrend man liest.
    if (this.handedOver || this.pendingEvent !== null) {
      this.counterScaleMarkers();
      return;
    }

    const missions = this.campaign.activeMissions;
    if (missions.length === 0) {
      // Im Lagebild vergeht keine Zeit von selbst. Der Spieler entscheidet,
      // wann die Uhr weiterlaeuft — durch Warten oder durch Auslaufen.
      //
      // Und die Kamera geht zurueck auf das ganze Dreieck: Ohne das bliebe sie
      // nach der letzten Ankunft auf einem stehenden Schiff kleben, und man
      // saehe von der Lage nichts mehr.
      if (this.manualLevel !== 'strategic') {
        this.manualLevel = 'strategic';
        this.applyZoom(true);
        this.updateCameraMode('strategic', true);
        this.redrawOverlays();
      }
      this.counterScaleMarkers();
      return;
    }

    const hours = (deltaMs / 1000) * GAME_HOURS_PER_SECOND;
    const { arrived, events } = this.campaign.advanceMissions(hours);

    this.announceNewIncidents();
    this.updateVesselMarkers();

    // Zoom und Kamera folgen der Fahrt, die gerade betrachtet wird.
    const focused = this.campaign.missionOf(this.focus) ?? missions[0];
    if (focused !== undefined) {
      this.director.update(focused.voyage.state.remainingNm);
      this.applyZoom(true);
      this.updateCameraMode(this.activeLevel(), true);
    }

    this.redrawOverlays();
    this.counterScaleMarkers();
    this.publishHudState();

    const pending = events[0];
    if (pending !== undefined) {
      this.raiseEvent(pending.mission, pending.event);
      return;
    }

    const first = arrived[0];
    if (first !== undefined) this.handOverToSite(first);
  }

  /** Setzt beide Fahrzeugmarker auf ihre aktuelle Position und Peilung. */
  private updateVesselMarkers(): void {
    for (const id of ['ship', 'submarine'] as const) {
      const marker = this.vesselMarkers.get(id);
      if (marker === undefined) continue;

      const mission = this.campaign.missionOf(id);
      const position = mission?.voyage.state.position ?? this.campaign.positionOf(id);
      const world = toWorld(position);
      marker.setPosition(world.x, world.y);

      if (mission !== null) {
        marker.setRotation(
          Phaser.Math.DegToRad(initialBearingDeg(position, this.targetOf(mission))),
        );
        this.recordWake(id, position);
      }

      // Das betrachtete Fahrzeug steht vorn, das andere tritt zurueck.
      marker.setAlpha(id === this.focus ? 1 : 0.55);
    }
  }

  /**
   * Laesst die Kampagnenzeit mitlaufen und meldet, was dabei hereinkommt.
   *
   * Ein neuer Notruf waehrend der Fahrt ist kein Ereignis, das eine
   * Entscheidung verlangt — aber seit M9 kann man darauf reagieren, indem man
   * das zweite Fahrzeug losschickt oder den Kurs wechselt. Er ist also eine
   * Nachricht mit Folgen.
   */
  private announceNewIncidents(): void {
    const current = this.campaign.activeIncidents;
    const fresh = current.filter((incident) => !this.knownIncidentIds.has(incident.id));
    const changed = fresh.length > 0 || current.length !== this.knownIncidentIds.size;
    if (!changed) return;

    this.knownIncidentIds = new Set(current.map((incident) => incident.id));
    this.refreshIncidentMarkers();

    const focused = this.campaign.missionOf(this.focus);
    const from = focused?.voyage.state.position ?? this.campaign.positionOf(this.focus);

    for (const incident of fresh) {
      audio.cue('alert');
      this.announce(
        t('map.newcall', {
          kind: t(incidentType(incident.kind).nameKey),
          name: incident.vesselName,
          miles: Math.round(distanceNm(from, incident.position)),
        }),
      );
    }

    this.announceLapsed();
  }

  /**
   * Meldet, was man hat verstreichen lassen.
   *
   * Bis M9 verschwand ein abgelaufener Notruf stillschweigend aus der Liste —
   * die einzige Stelle, an der das Spiel seine eigene Haerte wegdrueckte. Man
   * liess Menschen sterben und bekam dafuer eine kuerzere Liste. Jetzt steht
   * es da, mit Namen.
   */
  private announceLapsed(): void {
    for (const gone of this.campaign.takeLapsed()) {
      this.announce(
        t('map.lapsed', {
          kind: t(incidentType(gone.incident.kind).nameKey),
          name: gone.incident.vesselName,
          count: gone.incident.souls,
        }),
        true,
      );
    }
  }

  /** Kurze Einblendung ueber der Karte. */
  private announce(message: string, grave = false): void {
    const hud = this.scene.get('Hud') as HudScene | undefined;
    hud?.announce(message, grave);
  }

  /** Haelt alle Fahrten an und legt die Entscheidung vor. */
  private raiseEvent(mission: ActiveMission, event: VoyageEvent): void {
    this.pendingEvent = { mission, event };

    // Die Kamera geht zu dem Fahrzeug, das betroffen ist — sonst laege die
    // Entscheidung bei einem Schiff, das man gerade nicht ansieht.
    this.setFocus(mission.vesselId);

    audio.cue(
      event.kind === 'sonar_contact' || event.kind === 'derelict'
        ? 'sonar'
        : event.kind === 'second_call' || event.kind === 'station_silence'
          ? 'alert'
          : event.kind === 'mechanical' || event.kind === 'drifting_mine'
            ? 'creak'
            : 'select',
    );

    this.scene.launch('VoyageEvent', { event, vesselId: mission.vesselId });
    this.scene.pause();
  }

  /**
   * Nimmt die getroffene Entscheidung entgegen und schreibt ihre Kosten fort.
   *
   * Die Stunden sind der Punkt: Abzudrehen kann genau die Zeit kosten, die den
   * Ueberlebenden am Ziel gefehlt haette.
   */
  resolveEvent(choice: VoyageChoice): void {
    const pending = this.pendingEvent;
    this.pendingEvent = null;
    this.scene.resume();
    if (pending === null) return;

    this.campaign.applyVoyageChoice(pending.mission, choice);
  }

  /**
   * Ankunft — oder Stranden. Beides beendet *diese* Fahrt; die andere laeuft
   * weiter, sobald man von der Einsatzort-Szene zurueckkommt.
   */
  private handOverToSite(mission: ActiveMission): void {
    this.handedOver = true;
    audio.stopSonarSweep();
    audio.onPing = null;
    audio.setShallowWater(false);

    const state = mission.voyage.state;

    if (mission.homebound) {
      this.campaign.returnToBase(mission.vesselId, {
        arrivalHours: state.clockHours,
        fuelLeft: state.fuel,
        hullLeft: state.hull,
      });
      this.campaign.clearMission(mission.vesselId);
      // Kein Szenenwechsel noetig — die Karte ist ohnehin schon zu sehen.
      this.handedOver = false;
      this.setFocus(mission.vesselId);
      this.updateVesselMarkers();
      this.wake.set(mission.vesselId, []);
      this.setAudio();
      (this.scene.get('MapHud') as MapHudScene | undefined)?.refresh();
      return;
    }

    const result = this.campaign.resolveMission({
      incident: mission.incident as Incident,
      arrivalHours: state.clockHours,
      vessel: mission.voyage.profile,
      fuelLeft: state.fuel,
      hullLeft: state.hull,
      divertedFindings: mission.divertedFindings,
      divertedRescues: mission.divertedRescues,
    });
    this.campaign.clearMission(mission.vesselId);

    this.registry.set('missionResult', {
      ...result,
      stranded: state.stranded,
      // Womit gefahren wurde — die Einsatzort-Szene bietet damit die
      // Heimfahrt fuer das richtige Fahrzeug an.
      vesselId: mission.vesselId,
    });

    this.cameras.main.fadeOut(400, 5, 8, 13);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.scene.stop('Hud');
      this.scene.stop('MapHud');
      this.scene.start('Site');
    });
  }

  private activeLevel(): ZoomLevel {
    return this.manualLevel ?? this.director.current;
  }

  private applyZoom(animated: boolean): void {
    const target = zoomFactorFor(
      VIEWPORT_WIDTH_NM[this.activeLevel()],
      this.scale.width,
      this.scale.height,
    );

    if (Math.abs(target - this.appliedZoom) < 1e-4) return;
    this.appliedZoom = target;

    if (animated) {
      this.cameras.main.zoomTo(target, 650, 'Sine.easeInOut');
    } else {
      this.cameras.main.setZoom(target);
    }
  }

  /**
   * In der Uebersicht zeigt die Kamera die gesamte Seekarte und folgt dem Schiff
   * nicht — man plant dort einen Kurs und will die Karte ruhig vor sich haben.
   * Ab der Transitstufe uebernimmt wieder das Schiff.
   *
   * Die Kartengrenzen werden dabei bewusst aufgehoben: Ist das Sichtfeld
   * groesser als die Karte, klemmt Phaser sonst an die obere linke Ecke,
   * statt zu zentrieren.
   */
  private updateCameraMode(level: ZoomLevel, animated: boolean): void {
    if (level === this.cameraMode) return;
    this.cameraMode = level;

    // Tiefenlinien nur zeigen, solange die Textur nicht ueber ihre eigene
    // Auflösung hinaus vergroessert wird.
    const contourAlpha = level === 'tactical' ? 0 : 1;
    if (animated) {
      this.tweens.add({
        targets: this.contourLayer,
        alpha: contourAlpha,
        duration: 450,
        ease: 'Sine.easeInOut',
      });
    } else {
      this.contourLayer.setAlpha(contourAlpha);
    }

    const camera = this.cameras.main;
    const centreX = this.mapRect.x + this.mapRect.width / 2;
    const centreY = this.mapRect.y + this.mapRect.height / 2;

    if (level === 'strategic') {
      camera.stopFollow();
      camera.removeBounds();
      if (animated) {
        camera.pan(centreX, centreY, 650, 'Sine.easeInOut');
      } else {
        camera.centerOn(centreX, centreY);
      }
    } else {
      camera.setBounds(this.mapRect.x, this.mapRect.y, this.mapRect.width, this.mapRect.height);
      const marker = this.vesselMarkers.get(this.focus);
      if (marker !== undefined) camera.startFollow(marker, false, 0.08, 0.08);
    }
  }

  private nearestPlace(): { place: Place; distanceNm: number } {
    let best = ALL_PLACES[0] as Place;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const place of ALL_PLACES) {
      const d = distanceNm(this.focusPosition, place.position);
      if (d < bestDistance) {
        bestDistance = d;
        best = place;
      }
    }

    return { place: best, distanceNm: bestDistance };
  }

  // ---------------------------------------------------------------- Zeichnen

  /**
   * Kuestenlinien und Dreieck werden nur bei Stufenwechsel neu gezogen: Die
   * Linienstaerke muss dem Zoom entgegenwirken, damit sie auf dem Bildschirm
   * gleich stark bleibt, aber ein Neuzeichnen je Bild waere Verschwendung.
   */
  private redrawOverlays(): void {
    this.redrawStatic();
    this.redrawCourse();
  }

  /**
   * Kuestenlinien und Dreieck. Nur neu gezeichnet, wenn sich die Zoomstufe
   * geaendert hat — die Linienstaerke haengt daran, der Inhalt nicht.
   */
  private redrawStatic(): void {
    const level = this.activeLevel();

    if (level !== this.lastDrawnLevel) {
      this.lastDrawnLevel = level;
      const zoom = this.cameras.main.zoom || 1;
      const hairline = 1 / zoom;

      this.coastGraphics.clear();
      this.coastGraphics.lineStyle(hairline, PALETTE.coast, 0.9);
      for (const strand of this.mapData.coastline) {
        const points = strand.map(([lon, lat]) => toWorld({ lat, lon }));
        this.coastGraphics.beginPath();
        this.coastGraphics.moveTo(points[0]!.x, points[0]!.y);
        for (let i = 1; i < points.length; i++) {
          this.coastGraphics.lineTo(points[i]!.x, points[i]!.y);
        }
        this.coastGraphics.strokePath();
      }

      this.overlayGraphics.clear();
      this.overlayGraphics.lineStyle(hairline * 1.5, PALETTE.triangle, 0.85);
      const corners = [
        TRIANGLE_VERTICES.miami,
        TRIANGLE_VERTICES.sanJuan,
        TRIANGLE_VERTICES.bermuda,
      ].map(toWorld);
      this.overlayGraphics.beginPath();
      this.overlayGraphics.moveTo(corners[0]!.x, corners[0]!.y);
      this.overlayGraphics.lineTo(corners[1]!.x, corners[1]!.y);
      this.overlayGraphics.lineTo(corners[2]!.x, corners[2]!.y);
      this.overlayGraphics.closePath();
      this.overlayGraphics.strokePath();
    }
  }

  /**
   * Die gefahrenen Kurse samt Kielwasser — einer je laufender Fahrt.
   *
   * Gezeichnet wird der tatsaechliche Kurs, nicht die Luftlinie: Er fuehrt um
   * die Baenke herum, und genau das soll man sehen.
   */
  private redrawCourse(): void {
    this.courseLine.clear();
    const zoom = this.cameras.main.zoom || 1;

    for (const mission of this.campaign.activeMissions) {
      const focused = mission.vesselId === this.focus;
      const legs = [mission.voyage.state.position, ...mission.voyage.remainingWaypoints];
      if (legs.length < 2) continue;

      this.courseLine.lineStyle(1 / zoom, PALETTE.course, focused ? 0.65 : 0.3);
      this.courseLine.beginPath();
      const first = toWorld(legs[0] as LatLon);
      this.courseLine.moveTo(first.x, first.y);
      for (let i = 1; i < legs.length; i++) {
        const point = toWorld(legs[i] as LatLon);
        this.courseLine.lineTo(point.x, point.y);
      }
      this.courseLine.strokePath();

      // Kursaenderungen als kleine Marken — so liest sich die Route wie eine
      // abgesetzte Navigation und nicht wie ein gebogener Strich.
      this.courseLine.fillStyle(PALETTE.course, focused ? 0.8 : 0.4);
      for (let i = 1; i < legs.length - 1; i++) {
        const point = toWorld(legs[i] as LatLon);
        this.courseLine.fillCircle(point.x, point.y, 2 / zoom);
      }
    }

    this.drawWake(zoom);
  }

  /**
   * Haelt die gefahrene Spur fest — je Fahrzeug eine eigene.
   *
   * Nur alle paar Seemeilen einen Punkt; feiner braucht es die Linie nicht,
   * und die Liste bleibt kurz.
   */
  private recordWake(vesselId: VesselId, position: LatLon): void {
    const trail = this.wake.get(vesselId);
    if (trail === undefined) return;

    const last = trail[trail.length - 1];
    if (last !== undefined && distanceNm(last, position) < 2) return;

    trail.push(position);
    // Das Kielwasser verliert sich nach einer Weile; alles davor wird verworfen.
    if (trail.length > 160) trail.shift();
  }

  /** Zeichnet beide Kielwasser, nach hinten ausblendend. */
  private drawWake(zoom: number): void {
    this.wakeGraphics.clear();

    for (const [vesselId, trail] of this.wake) {
      if (trail.length < 2) continue;
      const dimmed = vesselId === this.focus ? 1 : 0.5;

      for (let i = 1; i < trail.length; i++) {
        // Je aelter das Segment, desto blasser.
        const age = i / trail.length;
        this.wakeGraphics.lineStyle(1 / zoom, PALETTE.vessel, (0.06 + age * 0.3) * dimmed);
        const from = toWorld(trail[i - 1] as LatLon);
        const to = toWorld(trail[i] as LatLon);
        this.wakeGraphics.beginPath();
        this.wakeGraphics.moveTo(from.x, from.y);
        this.wakeGraphics.lineTo(to.x, to.y);
        this.wakeGraphics.strokePath();
      }
    }
  }

  /** Marker sollen unabhaengig vom Zoom gleich gross erscheinen. */
  private counterScaleMarkers(): void {
    const inverse = 1 / (this.cameras.main.zoom || 1);
    for (const marker of this.vesselMarkers.values()) marker.setScale(inverse);
    for (const marker of this.placeMarkers) {
      marker.setScale(inverse);
    }
  }

  /** Die Position, auf die sich Anzeigen und Klang beziehen. */
  private get focusPosition(): LatLon {
    return (
      this.campaign.missionOf(this.focus)?.voyage.state.position ??
      this.campaign.positionOf(this.focus)
    );
  }

  private publishHudState(): void {
    const mission = this.campaign.missionOf(this.focus);
    if (mission === null) return;

    const voyageState = mission.voyage.state;
    const position = voyageState.position;
    const elevation = this.mapData.bathymetry.elevationAt(position);
    const nearest = this.nearestPlace();
    const conditions = mission.voyage.conditions();

    // Auf der Heimfahrt gibt es keine Stufe zu erreichen — dort steht ein
    // Strich, kein Versprechen.
    const projectedTier = mission.homebound
      ? 'home'
      : this.campaign.tierOnArrival(
          mission.incident as Incident,
          voyageState.clockHours + mission.voyage.estimate().hours,
        );

    // Die Flaeche folgt dem, was draussen passiert: schwere See und grosse
    // Tiefe machen sie praesenter.
    audio.setTension(
      Math.min(1, conditions.seaState / 9) * 0.6 +
        Math.min(1, Math.max(0, -elevation) / 6000) * 0.4,
    );

    // Ueber der Bahama-Bank knistern die Knallkrebse, in der Tiefsee nicht.
    audio.setShallowWater(Math.max(0, -elevation) < 200);

    // In der Tiefsee gelegentlich ein Ruf, den niemand zuordnen kann.
    if (Math.max(0, -elevation) > 2000 && Math.random() < 0.0006) {
      audio.biologic();
    }

    // Bei schwerer See aechzt der Rumpf — aber nur an der Oberflaeche.
    if (conditions.seaState > 5.5 && !audio.isSubmerged && Math.random() < 0.0025) {
      audio.cue('creak');
    }

    const state: HudState = {
      position,
      depthMetres: Math.max(0, -elevation),
      aground: elevation > 0,
      underway: !voyageState.arrived && !voyageState.stranded,
      speedKnots: conditions.effectiveKnots,
      nearestNameKey: nearest.place.nameKey,
      nearestDistanceNm: nearest.distanceNm,
      zoomLevel: this.activeLevel(),
      remainingNm: voyageState.remainingNm,
      fuel: voyageState.fuel,
      hull: voyageState.hull,
      seaStateKey: seaStateKey(conditions.seaState),
      projectedTier: `outcome.${projectedTier}`,
    };

    this.events.emit('hud', state);
  }
}
